import { Building2, FolderKanban, Globe, UserRound, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { useWatch, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { usePermissionCatalog } from '../api'
import { allCodes } from '../permissions'
import { ROLE_SCOPES, type Role, type RoleFormValues, type RoleScope } from '../types'
import { useAccessLookups } from '../useLookups'

const SCOPE_ICONS: Record<RoleScope, LucideIcon> = { organization: Globe, project: FolderKanban, department: Building2, own: UserRound }

const FieldError = ({ message }: { message?: string }) => (message ? <p className="text-xs text-destructive">{message}</p> : null)

export function RoleInfoFields({ form, roles, nameLocked }: { form: UseFormReturn<RoleFormValues>; roles: Role[]; nameLocked?: boolean }) {
  const { t } = useTranslation()
  const total = allCodes(usePermissionCatalog().data?.modules ?? []).length
  const { departments, projects } = useAccessLookups()
  const [template, setTemplate] = useState('')
  const [scope, scopeTargets, description] = useWatch({ control: form.control, name: ['scope', 'scopeTargets', 'description'] })
  const { errors } = form.formState

  const targets = scope === 'department' ? departments.filter((d) => d.status === 'active') : scope === 'project' ? projects : []

  const applyTemplate = (id: string) => {
    setTemplate(id)
    const source = roles.find((r) => r.id === id)
    if (source) form.setValue('permissions', [...source.permissions], { shouldDirty: true })
  }

  const setScope = (next: RoleScope) => {
    form.setValue('scope', next, { shouldDirty: true })
    form.setValue('scopeTargets', [], { shouldDirty: true })
    form.clearErrors('scopeTargets')
  }

  const toggleTarget = (id: string) => {
    const next = scopeTargets.includes(id) ? scopeTargets.filter((x) => x !== id) : [...scopeTargets, id]
    form.setValue('scopeTargets', next, { shouldDirty: true, shouldValidate: !!errors.scopeTargets })
  }

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="role-name">{t('access.form.name')}</Label>
        <Input id="role-name" placeholder={t('access.form.namePlaceholder')} aria-invalid={!!errors.name} disabled={nameLocked} {...form.register('name')} />
        {nameLocked ? <p className="text-xs text-muted-foreground">{t('access.form.systemNameLocked')}</p> : null}
        <FieldError message={errors.name?.message} />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="role-desc">{t('access.form.description')}</Label>
          <span className="text-xs tabular-nums text-muted-foreground">{description.length}/500</span>
        </div>
        <Textarea id="role-desc" rows={3} maxLength={500} placeholder={t('access.form.descriptionPlaceholder')} aria-invalid={!!errors.description} {...form.register('description')} />
        <FieldError message={errors.description?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="role-template">{t('access.form.template')}</Label>
        <Select
          id="role-template"
          value={template}
          onChange={applyTemplate}
          options={[
            { value: '', label: t('access.form.templateNone') },
            ...roles.map((r) => ({ value: r.id, label: r.name, description: t('access.summary.grantedOf', { granted: r.permissions.length, total }) })),
          ]}
        />
        <p className="text-xs text-muted-foreground">{t('access.form.templateHint')}</p>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('access.scope.label')}</legend>
        <p className="text-xs text-muted-foreground">{t('access.form.scopeHint')}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {ROLE_SCOPES.map((s) => {
            const Icon = SCOPE_ICONS[s]
            const on = scope === s
            return (
              <label
                key={s}
                className={cn(
                  'flex cursor-pointer gap-2.5 rounded-xl border p-3 transition-colors hover:border-brand-blue/40',
                  on && 'border-brand-blue bg-brand-blue/[0.06] ring-1 ring-brand-blue/30',
                )}
              >
                <input type="radio" name="role-scope" className="sr-only" checked={on} onChange={() => setScope(s)} />
                <Icon className={cn('mt-0.5 size-4 shrink-0', on ? 'text-brand-blue' : 'text-muted-foreground')} />
                <span>
                  <span className="block text-sm font-medium">{t(`access.scope.${s}`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`access.scope.${s}Hint`)}</span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>
      {targets.length ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t(scope === 'department' ? 'access.form.departments' : 'access.form.projects')}</p>
          <div className="flex flex-wrap gap-1.5">
            {targets.map((target) => {
              const on = scopeTargets.includes(target.id)
              return (
                <button
                  key={target.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleTarget(target.id)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    on ? 'border-brand-blue/40 bg-brand-blue/10 text-brand-blue' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {target.name}
                </button>
              )
            })}
          </div>
          <FieldError message={errors.scopeTargets?.message} />
        </div>
      ) : null}
    </>
  )
}
